import { Form } from "@unform/web";
import {
  Ban,
  Building,
  CheckCircle,
  CirclePlay,
  CircleX,
  MessageSquare,
  Pencil,
  X,
} from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import api from "~/services/api";
import { Zoom, toast } from "react-toastify";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { getRegistries, handleUpdatedFields } from "~/functions";
import { differenceInDays, format, parseISO } from "date-fns";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { FullGridContext } from "../..";
import EnrollmentProcess from "./EnrollmentProcess";
import PlacementTest from "./PlacementTest";
import I20Process from "./I20Process";
import TransferEligibility from "./TransferEligibility";
import Comments from "./Comments";

export const InputContext = createContext({});

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const pageAccess = {
    ...access,
    MenuHierarchyXGroup: { ...access.MenuHierarchyXGroup, edit: false },
  };
  const { handleOpened, successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    loaded: false,
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("enrollment-process");
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const generalForm = useRef();
  async function getPageData() {
    if (id !== "new") {
      try {
        const { data } = await api.get(`/enrollments/${id}`);
        setPageData({ ...data, loaded: true });
        const {
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        } = data;
        const registries = await getRegistries({
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        });
        setRegistry(registries);
        return true;
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        } else {
          toast("Failed to load enrollment.", { type: "error", autoClose: 3000 });
        }
        return false;
      }
    } else {
      setPageData({ ...pageData, loaded: true });
      setFormType("full");
    }
  }

  useEffect(() => {
    getPageData();
  }, []);

  async function handleRefresh() {
    if (refreshing || !successfullyUpdated || id === "new") return;
    setRefreshing(true);
    try {
      if (await getPageData()) {
        setRefreshVersion((version) => version + 1);
      }
    } finally {
      setRefreshing(false);
    }
  }

  async function handleGeneralFormSubmit(data) {
    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      return;
    }
    const updated = handleUpdatedFields(data, pageData);

    if (updated.length > 0) {
      const objUpdated = Object.fromEntries(updated);
      const { date_of_birth, visa_expiration } = objUpdated;
      try {
        await api.put(`/enrollments/${id}`, {
          ...objUpdated,
          date_of_birth: date_of_birth
            ? format(date_of_birth, "yyyy-MM-dd")
            : null,
          visa_expiration: visa_expiration
            ? format(visa_expiration, "yyyy-MM-dd")
            : null,
        });
        setPageData({ ...pageData, ...objUpdated });
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        handleOpened(null);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    }
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function handleInactivate() {}

  async function handleStartProcess(processType = "") {
    if (!processType) {
      return;
    }
    setLoading(true);
    try {
      await api.post(`/enrollments/start-process/`, {
        student_id: pageData.students.id,
        processType,
      });

      getPageData();
      toast("The process has been started!", {
        autoClose: 1000,
      });
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast("Error!", { autoClose: 1000 });
      console.log(err);
    }
  }

  async function handleStartPlacementTest() {
    setLoading(true);
    try {
      await api.post(`/enrollments/start-placement-test/`, {
        enrollment_id: pageData.id,
      });

      getPageData();
      toast("The process has been started!", {
        autoClose: 1000,
      });
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast("Error!", { autoClose: 1000 });
      console.log(err);
    }
  }

  async function handleI20Process() {
    api
      .post(`/enrollments/start-i20-process`, {
        enrollment_id: pageData.id,
      })
      .then(({ data }) => {
        toast("The process has been started!", {
          autoClose: 1000,
        });
        handleOpened(null);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast("Error!", { autoClose: 1000 });
        console.log(err);
      });
  }

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        formType === "preview" ? (
          <div className="border h-full rounded-xl overflow-hidden flex flex-col justify-start gap-1 overflow-y-scroll">
            <div className="relative bg-gray-100 h-16 px-4 py-2 flex flex-row items-start justify-start">
              <button
                onClick={() => setFormType("full")}
                className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <Pencil size={16} color="#fff" /> Open
              </button>
              <button
                onClick={() => handleOpened(null)}
                className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <X size={16} /> Close
              </button>
              <h2 style={{ fontSize: 24 }}>{pageData.name}</h2>
            </div>
            <div className="flex flex-1 flex-col items-left px-4 py-2 gap-1">
              <p className="border-b mb-1 pb-1">Prospect Information</p>
              <div className="flex flex-row items-center gap-1 text-xs">
                <strong>Name:</strong> {pageData.name}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-40 gap-4">
              {id !== "new" && (
                <RegisterFormMenu
                  setActiveMenu={setActiveMenu}
                  activeMenu={activeMenu}
                  name="enrollment-process"
                >
                  {pageData.enrollmentProcess ? (
                    <CirclePlay size={16} />
                  ) : (
                    <CheckCircle size={16} />
                  )}{" "}
                  Enrollment
                </RegisterFormMenu>
              )}

              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="placement-test"
                disabled={pageData.status === "Inactive"}
                messageOnDisabled="This enrollment is inactivated."
              >
                {!pageData?.placement_test_status ||
                pageData?.placement_test_status === "Not started" ? (
                  <CircleX size={16} />
                ) : pageData?.placement_test_status === "finished" ? (
                  <CheckCircle size={16} />
                ) : (
                  <CirclePlay size={16} />
                )}{" "}
                Placement Test
              </RegisterFormMenu>

              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="i20-process"
                disabled={pageData.status === "Inactive"}
                messageOnDisabled="This enrollment is inactivated."
              >
                {!pageData?.i20form ? (
                  <CircleX size={16} />
                ) : (
                  <CirclePlay size={16} />
                )}{" "}
                DSO Process
              </RegisterFormMenu>
              {(pageData?.contract?.processsubstatus_id === 4 ||
                pageData?.contract?.processsubstatus_id === 3) && (
                <RegisterFormMenu
                  setActiveMenu={setActiveMenu}
                  activeMenu={activeMenu}
                  name="transfer-eligibility"
                  disabled={pageData.status === "Inactive"}
                  messageOnDisabled="This enrollment is inactivated."
                >
                  {pageData.enrollmenttransfers?.dso_signature ? (
                    <CheckCircle size={16} />
                  ) : pageData.enrollmenttransfers ? (
                    <CirclePlay size={16} />
                  ) : (
                    <CircleX size={16} />
                  )}{" "}
                  Transfer Eligibility
                </RegisterFormMenu>
              )}

              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="timeline"
              >
                <Building size={16} /> Timeline
              </RegisterFormMenu>

              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="comments"
              >
                <MessageSquare size={16} /> Comments
              </RegisterFormMenu>
            </div>
            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
                <Form
                  key={refreshVersion}
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  <InputContext.Provider
                    value={{
                      id,
                      generalForm,
                      setSuccessfullyUpdated,
                      fullscreen,
                      setFullscreen,
                      successfullyUpdated,
                      handleCloseForm,
                      handleInactivate,
                      canceled: pageData.canceled_at,
                    }}
                  >
                    {pageData.loaded ? (
                      <>
                        <FormHeader
                          access={pageAccess}
                          title={
                            pageData.students.name +
                            " " +
                            pageData.students.last_name
                          }
                          registry={registry}
                          InputContext={InputContext}
                          disableSave={pageData.status === "Inactive"}
                          onRefresh={id !== "new" ? handleRefresh : null}
                          refreshing={refreshing}
                          refreshDisabled={!successfullyUpdated}
                        />
                        {pageData.status === "Inactive" && (
                          <div className="flex items-center gap-3 mx-4 mt-3 mb-1 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            <Ban size={16} className="shrink-0" />
                            <span>
                              <strong>Enrollment Inactivated</strong> — This enrollment has been inactivated. A new enrollment must be created.
                            </span>
                          </div>
                        )}

                        {activeMenu === "timeline" && (
                          <InputLineGroup
                            title="TIMELINE"
                            activeMenu={activeMenu === "timeline"}
                          >
                            <div className="px-4 w-full flex flex-col">
                              <h2 className="text-xl font-bold w-full text-left pb-4">
                                {pageData.contract.processtypes?.name}
                                {" - "}
                                {pageData.contract.processsubstatuses?.name}
                              </h2>
                              <div className="w-full flex flex-col items-center justify-start text-center border border-gray-200 border-b-0">
                                {pageData?.enrollmenttimelines
                                  ?.sort((a, b) =>
                                    b.created_at > a.created_at ? 1 : -1
                                  )
                                  .map((timeline, index) => {
                                    const showPhase =
                                      index === 0 ||
                                      timeline.phase !==
                                        pageData.enrollmenttimelines[index - 1]
                                          .phase
                                        ? true
                                        : false;

                                    const showPhaseStep =
                                      index === 0 ||
                                      timeline.phase_step !==
                                        pageData.enrollmenttimelines[index - 1]
                                          .phase_step
                                        ? true
                                        : false;

                                    return (
                                      <>
                                        {showPhase ? (
                                          <div className="px-4 py-2 w-full flex flex-col items-start justify-start text-center border-b bg-slate-100 gap-2">
                                            <h2 className="text-md font-bold w-full text-left">
                                              {timeline.phase}
                                            </h2>
                                          </div>
                                        ) : null}

                                        {showPhaseStep ? (
                                          <div className="px-8 py-2 w-full flex flex-col items-start justify-start text-center border-b bg-slate-50 gap-2">
                                            <h2 className="text-sm font-bold w-full text-left ">
                                              {timeline.phase_step}
                                            </h2>
                                          </div>
                                        ) : null}

                                        <div className="relative px-12 py-2 w-full flex flex-col items-start justify-start text-center border-b gap-2">
                                          <p>{timeline.step_status}</p>
                                          <span className="text-xs text-gray-500">
                                            {format(
                                              timeline.created_at,
                                              "MM/dd/yyyy"
                                            ) +
                                              " at " +
                                              format(
                                                timeline.created_at,
                                                "HH:mm"
                                              )}
                                          </span>
                                          {index === 0 &&
                                            timeline.expected_date && (
                                              <div className="w-full flex flex-row items-center justify-start text-center gap-2 border-t border-dashed pt-1">
                                                <span className="relative flex h-2 w-2">
                                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-mila_orange opacity-75"></span>
                                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-mila_orange"></span>
                                                </span>

                                                <p className="text-xs ">
                                                  Expected to{" "}
                                                  {format(
                                                    parseISO(
                                                      timeline.expected_date
                                                    ),
                                                    "MM/dd/yyyy"
                                                  )}{" "}
                                                  {differenceInDays(
                                                    parseISO(
                                                      timeline.expected_date
                                                    ),
                                                    new Date()
                                                  ) < 0 && (
                                                    <>
                                                      <span className="text-red-500">
                                                        - Delayed by{" "}
                                                        {differenceInDays(
                                                          parseISO(
                                                            timeline.expected_date
                                                          ),
                                                          new Date()
                                                        ) * -1}{" "}
                                                        days
                                                      </span>
                                                    </>
                                                  )}
                                                </p>
                                              </div>
                                            )}
                                        </div>
                                      </>
                                    );
                                  })}
                              </div>
                            </div>
                          </InputLineGroup>
                        )}
                        {/* <InputLineGroup
                          title="TIMELINE"
                          activeMenu={activeMenu === "timeline"}
                        >
                          {pageData.enrollmenttimelines &&
                            pageData.enrollmenttimelines.length > 0 &&
                            pageData.enrollmenttimelines.map(
                              (timeline, index) => {
                                return (
                                  <Scope
                                    key={index}
                                    path={`enrollmenttimelines[${index}]`}
                                  >
                                    <InputLine
                                      title={format(
                                        timeline.created_at,
                                        "MM/dd/yyyy @ HH:mm"
                                      )}
                                    >
                                      <Scope
                                        path={`students.processsubstatuses`}
                                      >
                                        <Input
                                          type="text"
                                          name="name"
                                          grow
                                          title="Sub Status"
                                          defaultValue={
                                            pageData.students.processsubstatuses
                                              .name
                                          }
                                          InputContext={InputContext}
                                        />
                                      </Scope>
                                      <Input
                                        type="text"
                                        name="phase"
                                        grow
                                        title="Phase"
                                        defaultValue={timeline.phase}
                                        InputContext={InputContext}
                                      />
                                      <Input
                                        type="text"
                                        name="phase_step"
                                        grow
                                        title="Phase Step"
                                        defaultValue={timeline.phase_step}
                                        InputContext={InputContext}
                                      />
                                      <Input
                                        type="text"
                                        name="step_status"
                                        grow
                                        title="Step Status"
                                        defaultValue={timeline.step_status}
                                        InputContext={InputContext}
                                      />
                                      <DatePicker
                                        name="expected_date"
                                        grow
                                        title="Expected Date"
                                        defaultValue={
                                          timeline.expected_date
                                            ? parseISO(timeline.expected_date)
                                            : null
                                        }
                                        placeholderText="MM/DD/YYYY"
                                        InputContext={InputContext}
                                      />
                                    </InputLine>
                                  </Scope>
                                );
                              }
                            )}
                        </InputLineGroup> */}

                        {activeMenu === "enrollment-process" && (
                          <InputLineGroup
                            title="Enrollment Process"
                            activeMenu={activeMenu === "enrollment-process"}
                          >
                            <EnrollmentProcess
                              enrollment={pageData}
                              student_id={pageData.student_id}
                              loading={loading}
                              setLoading={setLoading}
                              handleStartProcess={handleStartProcess}
                              disabled={pageData.status === "Inactive"}
                            />
                          </InputLineGroup>
                        )}
                        <InputLineGroup
                          title="Placement Text"
                          activeMenu={activeMenu === "placement-test"}
                        >
                          <PlacementTest
                            enrollment={pageData}
                            handleStartProcess={handleStartPlacementTest}
                          />
                        </InputLineGroup>
                        <InputLineGroup
                          title="DSO Process"
                          activeMenu={activeMenu === "i20-process"}
                        >
                          <I20Process
                            enrollment={pageData.i20form}
                            handleStartProcess={handleI20Process}
                          />
                        </InputLineGroup>

                        {activeMenu === "transfer-eligibility" && (
                          <InputLineGroup
                            title="Transfer Eligibility"
                            activeMenu={activeMenu === "transfer-eligibility"}
                          >
                            <TransferEligibility
                              access={access}
                              enrollment={pageData}
                              setLoading={setLoading}
                              loading={loading}
                              handleStartProcess={handleStartProcess}
                            />
                          </InputLineGroup>
                        )}

                        {activeMenu === "comments" && (
                          <InputLineGroup
                            title="Comments"
                            activeMenu={activeMenu === "comments"}
                          >
                            <Comments enrollment_id={id} />
                          </InputLineGroup>
                        )}
                      </>
                    ) : (
                      <FormLoading />
                    )}
                  </InputContext.Provider>
                </Form>
              </div>
            </div>
          </div>
        )
      ) : null}
    </Preview>
  );
}
